import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BulkUploadModal from './components/BulkUploadModal';

const DataManagement = () => {
  const navigate = useNavigate();
  const [showUploadModal, setShowUploadModal] = useState(false);

  const managementSections = [
    {
      id: 'products',
      title: 'Product Management',
      description: 'Manage inventory, pricing, and product catalog',
      icon: 'Package',
      path: '/products',
      color: 'bg-blue-500',
      stats: { total: 'Catalog Items', count: '150+' }
    },
    {
      id: 'technicians',
      title: 'Personnel Management',
      description: 'Manage technician profiles, skills, and availability',
      icon: 'UserCog',
      path: '/personnel-management',
      color: 'bg-green-500',
      stats: { total: 'Active Technicians', count: '25' }
    },
    {
      id: 'customers',
      title: 'Customer Management',
      description: 'Manage customer database, contacts, and service history',
      icon: 'Users',
      path: '/customer-database',
      color: 'bg-purple-500',
      stats: { total: 'Active Customers', count: '500+' }
    }
  ];

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Data Management
              </h1>
              <p className="text-muted-foreground">
                Centralized hub for managing products, technicians, and customers
              </p>
            </div>
            <Button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2"
            >
              <Icon name="Upload" size={18} color="currentColor" />
              Bulk Upload Data
            </Button>
          </div>
        </div>

        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {managementSections?.map((section) => (
            <div
              key={section?.id}
              className="bg-card rounded-lg shadow-elevation-2 p-6 hover:shadow-elevation-3 transition-all cursor-pointer border border-border"
              onClick={() => handleNavigate(section?.path)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`${section?.color} w-12 h-12 rounded-lg flex items-center justify-center`}>
                  <Icon name={section?.icon} size={24} color="white" />
                </div>
                <Icon name="ArrowRight" size={20} color="var(--color-muted-foreground)" />
              </div>
              
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                {section?.title}
              </h3>
              
              <p className="text-sm text-muted-foreground mb-4">
                {section?.description}
              </p>
              
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <span className="text-xs text-muted-foreground">{section?.stats?.total}</span>
                <span className="text-lg font-semibold text-foreground">{section?.stats?.count}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions Section */}
        <div className="mt-8 bg-card rounded-lg shadow-elevation-2 p-6 border border-border">
          <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/products')}
              className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted transition-smooth text-left"
            >
              <Icon name="Plus" size={20} color="var(--color-primary)" />
              <span className="text-sm font-medium text-foreground">Add Product</span>
            </button>
            <button
              onClick={() => navigate('/personnel-management')}
              className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted transition-smooth text-left"
            >
              <Icon name="UserPlus" size={20} color="var(--color-primary)" />
              <span className="text-sm font-medium text-foreground">Add Technician</span>
            </button>
            <button
              onClick={() => navigate('/customer-database')}
              className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted transition-smooth text-left"
            >
              <Icon name="UserPlus" size={20} color="var(--color-primary)" />
              <span className="text-sm font-medium text-foreground">Add Customer</span>
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted transition-smooth text-left"
            >
              <Icon name="FileUp" size={20} color="var(--color-primary)" />
              <span className="text-sm font-medium text-foreground">Import Data</span>
            </button>
          </div>
        </div>
      </main>

      {showUploadModal && (
        <BulkUploadModal onClose={() => setShowUploadModal(false)} />
      )}
    </div>
  );
};

export default DataManagement;